import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Logo from './Logo';

const routePrefetchers = {
  '/workshops': () => import('../pages/Workshops'),
  '/shop': () => import('../pages/Shop'),
  '/about': () => import('../pages/About'),
  '/contact': () => import('../pages/Contact')
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  const handlePrefetch = (path) => {
    if (routePrefetchers[path]) {
      routePrefetchers[path]();
    }
  };

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/workshops', label: t('workshops') },
    { path: '/shop', label: t('shop') },
    { path: '/about', label: t('about') },
    { path: '/contact', label: t('contact') }
  ];

  return (
    <header className="fixed top-0 w-full bg-cream-white/95 backdrop-blur-md z-50 border-b border-soft-gray">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group flex-shrink-0 max-w-[75%] sm:max-w-none">
            <Logo size="md" className="transition-transform group-hover:scale-105" />
            <div className="flex flex-col min-w-0">
              <span className="font-serif text-xl sm:text-2xl text-charcoal tracking-tight leading-none whitespace-nowrap truncate">Unya Mee</span>
              <span className="text-[10px] sm:text-xs uppercase tracking-widest text-warm-gray whitespace-nowrap truncate">Sustainable Fashion</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onMouseEnter={() => handlePrefetch(link.path)}
                onTouchStart={() => handlePrefetch(link.path)}
                className={`text-sm uppercase tracking-widest transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-warm-sage border-b-2 border-warm-sage font-medium'
                    : 'text-charcoal hover:text-warm-sage'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={toggleLanguage}
              aria-label="Switch Language"
              className="flex items-center space-x-1.5 text-charcoal hover:text-warm-sage transition-colors duration-300 px-2 py-1 rounded"
            >
              <Globe size={18} />
              <span className="text-sm uppercase tracking-widest font-medium">{language}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden text-charcoal hover:text-warm-sage transition-colors p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-warm-sage"
          >
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-6 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onMouseEnter={() => handlePrefetch(link.path)}
                onTouchStart={() => handlePrefetch(link.path)}
                onClick={() => setIsMenuOpen(false)}
                className={`block text-sm uppercase tracking-widest transition-colors ${
                  location.pathname === link.path
                    ? 'text-warm-sage'
                    : 'text-charcoal hover:text-warm-sage'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => {
                toggleLanguage();
                setIsMenuOpen(false);
              }}
              className="flex items-center space-x-2 text-charcoal hover:text-warm-sage transition-colors"
            >
              <Globe size={18} />
              <span className="text-sm uppercase tracking-widest">
                {language === 'en' ? 'Polski' : 'English'}
              </span>
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;